import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { NotificationService } from '../../../../Services/notification.service';
import { Router, RouterLink } from '@angular/router';
import { inject } from '@angular/core';
import { FormsModule } from '@angular/forms'; // Import FormsModule
import autoTable from 'jspdf-autotable'; // For PDF export
import * as XLSX from 'xlsx'; // For Excel export
import jsPDF from 'jspdf'; // For PDF export
import FileSaver from 'file-saver'; // For file saving
import { StockOutService } from '../../../../Services/Stock_Transactions/Stock_out/stock-out.service'; // Import the new service
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-stock-out-list',
  standalone: true,
  imports: [NgFor, FormsModule, NgIf],
  templateUrl: './stock-out-list.component.html',
  styleUrl: './stock-out-list.component.css',
})
export class StockOutListComponent implements OnInit {
  paginatedStockOutList: any[] = [];
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;
  stockOutList: any[] = []; // Initialize as an empty array

  // New properties for filtering, sorting, and column selection
  searchValues: { [key: string]: string } = {};
  sortKey: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  showDropdown: boolean = false;
  globalSearchText: string = '';
  startDate: string = '';
  endDate: string = '';
  filteredStockOutList: any[] = [];


  // Define the default set of fields for stock out products
  defaultFields = [
    { key: 'stout_image', label: 'Image', selected: true, isImage: true },
    { key: 'stout_item_code', label: 'Item Code', selected: true },
    { key: 'stout_metal_type', label: 'Metal Type', selected: true },
    { key: 'stout_product_type', label: 'Product Type', selected: true },
    { key: 'stout_stock_type', label: 'Stock Type', selected: true },
    { key: 'stout_item_category', label: 'Item Category', selected: true },
    { key: 'stout_item_name', label: 'Item Name', selected: true },
    { key: 'stout_item_size', label: 'Item Size', selected: true },
    { key: 'stout_color', label: 'Color', selected: true },
    { key: 'stout_purity', label: 'Purity', selected: true },
    { key: 'stout_quantity', label: 'Quantity', selected: true },
    { key: 'stout_metal_rate', label: 'Metal Rate', selected: true },
    { key: 'stout_item_length', label: 'Item Length', selected: true },
    { key: 'stout_item_width', label: 'Item Width', selected: true },
    { key: 'stout_unit_price', label: 'Unit Price', selected: true },
    { key: 'stout_unit_quantity', label: 'Unit Quantity', selected: true },
    { key: 'stout_sub_unit_price', label: 'Sub Unit Price', selected: true },
    { key: 'stout_total_wt', label: 'Total Weight', selected: true },
    { key: 'stout_packaging_unit_cost', label: 'Packaging Unit Cost', selected: true },
    { key: 'stout_gs_weight', label: 'GS Weight', selected: true },
    { key: 'stout_pkt_weight', label: 'PKT Weight', selected: true },
    { key: 'stout_less_weight', label: 'Less Weight', selected: true },
    { key: 'stout_cust_wastage_weight', label: 'Cust Wastage Weight', selected: true },
    { key: 'stout_net_weight', label: 'Net Weight', selected: true },
    { key: 'stout_tag_weight', label: 'Tag Weight', selected: true },
    { key: 'stout_fine_weight', label: 'Fine Weight', selected: true },
    { key: 'stout_final_fine_weight', label: 'Final Fine Weight', selected: true },
    { key: 'stout_making_charges', label: 'Making Charges', selected: true },
    { key: 'stout_lab_charges', label: 'Lab Charges', selected: true },
    { key: 'stout_per_gram_labour', label: 'Per Gram Labour', selected: true },
    { key: 'stout_per_piece_labour', label: 'Per Piece Labour', selected: true },
    { key: 'stout_per_gm_shipping', label: 'Per GM Shipping', selected: true },
    { key: 'stout_per_piece_shipping', label: 'Per Piece Shipping', selected: true },
    { key: 'stout_total_per_piece_price', label: 'Total Per Piece Price', selected: true },
    { key: 'stout_per_piece_silver_price', label: 'Per Piece Silver Price', selected: true },
    { key: 'stout_silver_rate', label: 'Silver Rate', selected: true },
    { key: 'stout_per_piece_weight', label: 'Per Piece Weight', selected: true },
    { key: 'stout_subunit', label: 'Subunit', selected: true },
  ];

  selectedFields: any[] = [];
  titleTag: boolean = true; // Retain titleTag for consistency if needed

  constructor(
    private stockOutService: StockOutService, // Use the new service
    private notificationService: NotificationService
  ) { }

  router = inject(Router);

  ngOnInit(): void {
    this.loadSelectedFields();
    this.getStockOutList();
    this.hideTitle();
  }

  hideTitle() {
    const currentUrl = this.router.url;
    // Adjust this logic if 'stock-out' can also be part of a report URL
    if (currentUrl.includes('report')) {
      this.titleTag = false;
    }
  }

  loadSelectedFields() {
    const stockOutSelection = localStorage.getItem('stockOutSelected');
    if (stockOutSelection) {
      const storedFields = JSON.parse(stockOutSelection);
      const storedFieldMap = new Map(
        storedFields.map((field: any) => [field.key, field.selected])
      );

      this.selectedFields = this.defaultFields.map((defaultField) => ({
        ...defaultField,
        selected: storedFieldMap.has(defaultField.key)
          ? storedFieldMap.get(defaultField.key)
          : defaultField.selected,
      }));
    } else {
      this.selectedFields = [...this.defaultFields];
    }
  }

  saveSelectedFields() {
    localStorage.setItem('stockOutSelected', JSON.stringify(this.selectedFields));
  }

  getStockOutList() {
    const params = new HttpParams();
    this.stockOutService.getStockOutEntries(params).subscribe(
      (response: any) => {
        if (response?.data?.length) {
          this.stockOutList = response.data.reverse();
          this.filterTable(); // Apply filters and pagination after fetching data
        } else {
          this.stockOutList = [];
          this.paginatedStockOutList = [];
          this.totalPages = 0;
        }
      },
      (error: any) => {
        this.notificationService.showError(
          'Failed to fetch stock out entries.',
          'Error'
        );
        this.stockOutList = [];
        this.paginatedStockOutList = [];
        this.totalPages = 0;
      }
    );
  }

  deleteStockOut(id: any): void {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this stock out entry?'
    );
    if (confirmDelete) {
      // Assuming a delete method exists in your StockOutService
      // You'll need to implement this in StockOutService if it doesn't exist yet
      // Example: this.stockOutService.deleteStockOut(id).subscribe(...)
      this.notificationService.showError(
        'Delete functionality not yet implemented in StockOutService.',
        'Not Implemented'
      );
      // For now, simulating success/failure or remove this block
      // this.stockOutService.deleteStockOut(id).subscribe(
      //   (response: any) => {
      //     this.notificationService.showSuccess('Stock out entry deleted successfully!', 'Success');
      //     this.getStockOutList(); // Re-fetch and re-filter
      //   },
      //   (error: any) => {
      //     this.notificationService.showError('Failed to delete stock out entry. Please try again.', 'Error');
      //   }
      // );
    }
  }

  editStockOut(id: any) {
    // Implement navigation or modal for editing if needed
    // Example: this.router.navigate(['/stock-out-edit', id]);
  }

  filterTable() {

    if (!this.stockOutList) return;

    let filtered = this.stockOutList.filter((item: any) => {

      /* ================= DATE FILTER ================= */

      if (this.startDate || this.endDate) {

        const itemDateRaw = item['stout_bill_date']; // ✅ FIXED FIELD

        if (!itemDateRaw) {
          console.log('❌ No stout_bill_date found');
          return false;
        }

        const itemDate = itemDateRaw; // already YYYY-MM-DD format

        if (this.startDate && itemDate < this.startDate) {
          return false;
        }

        if (this.endDate && itemDate > this.endDate) {
          return false;
        }
      }


      /* ================= COLUMN FILTER ================= */

      const matchesFieldFilters = this.selectedFields.every((field) => {

        if (!field.selected) return true;

        const searchTerm = (this.searchValues[field.key] || '')
          .trim()
          .toLowerCase();

        if (!searchTerm) return true;

        const fieldValue = (item[field.key] ?? '')
          .toString()
          .toLowerCase();

        return fieldValue.includes(searchTerm);
      });

      /* ================= GLOBAL SEARCH ================= */

      const matchesGlobalSearch = this.globalSearchText
        ? Object.values(item).some((val) =>
          (val ?? '')
            .toString()
            .toLowerCase()
            .includes(this.globalSearchText.trim().toLowerCase())
        )
        : true;

      return matchesFieldFilters && matchesGlobalSearch;
    });

    /* ================= SORT ================= */

    if (this.sortKey) {
      filtered.sort((a: any, b: any) => {

        const valA = a[this.sortKey] ?? '';
        const valB = b[this.sortKey] ?? '';

        return this.sortDirection === 'asc'
          ? valA > valB ? 1 : valA < valB ? -1 : 0
          : valA < valB ? 1 : valA > valB ? -1 : 0;
      });
    }

    /* ================= SAVE FILTERED ================= */

    this.filteredStockOutList = filtered;

    /* ================= PAGINATION ================= */

    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
    this.currentPage = Math.min(this.currentPage, this.totalPages || 1);

    const start = (this.currentPage - 1) * this.itemsPerPage;

    this.paginatedStockOutList = filtered.slice(
      start,
      start + this.itemsPerPage
    );
  }


  sortBy(key: string) {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDirection = 'asc';
    }
    this.filterTable();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.filterTable();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.filterTable();
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.filterTable();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.filterTable();
    }
  }

  get pageNumbers(): number[] {
    const pageNumbers: number[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      if (this.currentPage > 2) {
        pageNumbers.push(-1);
      }
      let start = Math.max(
        2,
        this.currentPage - Math.floor(maxPagesToShow / 2) + 1
      );
      let end = Math.min(
        this.totalPages - 1,
        this.currentPage + Math.floor(maxPagesToShow / 2) - 1
      );
      if (this.currentPage <= Math.floor(maxPagesToShow / 2) + 1) {
        end = Math.min(this.totalPages - 1, maxPagesToShow - 1);
      }
      if (this.currentPage >= this.totalPages - Math.floor(maxPagesToShow / 2)) {
        start = Math.max(2, this.totalPages - maxPagesToShow + 2);
      }
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
      if (this.currentPage < this.totalPages - 1) {
        if (
          pageNumbers[pageNumbers.length - 1] !== this.totalPages &&
          pageNumbers[pageNumbers.length - 1] !== -1
        ) {
          pageNumbers.push(-1);
        }
      }
      if (!pageNumbers.includes(this.totalPages) && this.totalPages > 1) {
        pageNumbers.push(this.totalPages);
      }
    }
    const uniquePageNumbers = Array.from(new Set(pageNumbers)).sort(
      (a, b) => a - b
    );
    return uniquePageNumbers;
  }

  get allFieldsSelected(): boolean {
    return this.selectedFields.every(field => field.selected);
  }

  get noFieldsSelected(): boolean {
    return this.selectedFields.every(field => !field.selected);
  }

  selectAllFields() {
    this.selectedFields.forEach((field) => (field.selected = true));
    this.saveSelectedFields();
    this.filterTable();
  }

  deselectAllFields() {
    this.selectedFields.forEach((field) => (field.selected = false));
    this.saveSelectedFields();
    this.filterTable();
  }

  get selectedFieldss() {
    return this.selectedFields.filter((field) => field.selected);
  }

  applyColumnSelection() {
    this.saveSelectedFields();
    this.showDropdown = false;
    this.filterTable();
  }
  async downloadPDF(): Promise<void> {
    await this.preparePdfImages();

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3',
    });

    const selectedFields = this.selectedFields.filter((f) => f.selected);

    if (!selectedFields.length) {
      this.notificationService.showError(
        'Please select at least one column',
        'Error'
      );
      return;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    /* ================= HEADER ================= */

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');

    doc.text('Stock Out Report', pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth - 20,
      10,
      { align: 'right' }
    );

    /* ================= TABLE ================= */

    const headers = [
      { header: 'No.', dataKey: 'no' },
      ...selectedFields.map((f) => ({
        header: f.label,
        dataKey: f.key,
      })),
    ];

    const sourceData = this.getExportSourceData();

    const body = sourceData.map((item, rowIndex) => {

      const row: any = {
        no: rowIndex + 1,
      };

      selectedFields.forEach((f) => {
        if (f.key === 'stout_image') {
          row[f.key] = '';
          row.__img = item.__img; // Use preloaded image
        } else {
          row[f.key] = this.getDisplayValue(item[f.key]);
        }
      });

      return row;
    });

    autoTable(doc, {
      columns: headers,
      body,

      startY: 22,

      margin: { left: 6, right: 6 },

      theme: 'grid',

      horizontalPageBreak: false,
      rowPageBreak: 'auto',

      tableWidth: pageWidth - 12,

      /* ---------- STYLES ---------- */

      styles: {
        fontSize: 6.2,
        cellPadding: 1.3,
        overflow: 'linebreak',
        valign: 'middle',
      },

      headStyles: {
        fillColor: [193, 151, 37],
        textColor: 255,
        halign: 'center',
        fontSize: 7,
        fontStyle: 'bold',
      },

      bodyStyles: {
        textColor: 40,
      },

      columnStyles: {
        no: {
          cellWidth: 12,
          halign: 'center',
        },
        stout_image: {
          cellWidth: 20,
        },
      },

      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },

      didDrawCell: (data: any) => {
        if (data.column.dataKey === 'stout_image' && data.section === 'body') {
          const rowData: any = data.row.raw;
          const img = rowData?.__img;
          if (img) {
            try {
              const padding = 1;
              const dim = Math.min(
                data.cell.width - padding * 2,
                data.cell.height - padding * 2
              );
              const xInfo = data.cell.x + (data.cell.width - dim) / 2;
              const yInfo = data.cell.y + (data.cell.height - dim) / 2;

              doc.addImage(img, 'JPEG', xInfo, yInfo, dim, dim);
            } catch (err) {
              console.error('Error drawing image in PDF:', err);
            }
          }
        }
      },

      didDrawPage: () => {
        doc.setFontSize(8);

        doc.text(
          `Page ${(doc as any).getNumberOfPages()}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      },
    });

    doc.save('stock-out-report.pdf');
  }



  downloadExcel(): void {
    const selectedFields = this.selectedFields.filter(
      (field) => field.selected
    );

    // ✅ numeric backend keys
    const numericKeys = [
      'stout_purity',
      'stout_quantity',
      'stout_metal_rate',
      'stout_item_length',
      'stout_item_width',
      'stout_unit_price',
      'stout_unit_quantity',
      'stout_sub_unit_price',
      'stout_total_wt',
      'stout_gs_weight',
      'stout_pkt_weight',
      'stout_less_weight',
      'stout_cust_wastage_weight',
      'stout_net_weight',
      'stout_tag_weight',
      'stout_fine_weight',
      'stout_final_fine_weight',
      'stout_making_charges',
      'stout_lab_charges',
      'stout_per_gram_labour',
      'stout_per_piece_labour',
      'stout_per_gm_shipping',
      'stout_per_piece_shipping',
      'stout_total_per_piece_price',
      'stout_per_piece_silver_price',
      'stout_silver_rate',
      'stout_per_piece_weight'
    ];

    const sourceData = this.getExportSourceData();

    const data = sourceData.map((item, index) => {

      const row: any = { 'No.': index + 1 };

      selectedFields.forEach((field) => {
        let value: any = item[field.key];

        // ✅ force numeric fields
        if (numericKeys.includes(field.key)) {
          value =
            value !== null &&
              value !== undefined &&
              value !== ''
              ? Number(value)
              : 0;
        }

        row[field.label] = value;
      });

      return row;
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    // ✅ force number cell types
    Object.keys(worksheet).forEach((cell) => {
      if (cell.startsWith('!')) return;

      const c = worksheet[cell];
      if (typeof c.v === 'number') {
        c.t = 'n';
      }
    });

    const workbook: XLSX.WorkBook = {
      Sheets: { 'Stock Out List': worksheet },
      SheetNames: ['Stock Out List'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'stock-out-list.xlsx'
    );
  }


  downloadJSONAll(): void {
    const data = this.getExportSourceData();
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const fileName = 'stock-out-list.json';
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  downloadCSV(): void {
    const data = this.getExportSourceData();
    if (!data.length) return;

    const selectedFields = this.selectedFields?.filter((f) => f.selected) || [];
    const headers = ['No.', ...selectedFields.map((f) => f.label)];
    const keys = selectedFields.map((f) => f.key);

    let csvContent = headers.join(',') + '\n';
    data.forEach((item, index) => {
      const row = [
        index + 1,
        ...keys.map((key) => {
          const value = this.getDisplayValue(item[key]);
          return typeof value === 'string' && value.includes(',')
            ? `"${value}"`
            : value ?? '';
        }),
      ];
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = 'stock-out-list.csv';
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    link.click();
    URL.revokeObjectURL(link.href);
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  restoreColumnSelection() {
    localStorage.removeItem('stockOutSelected');
    this.selectedFields = [...this.defaultFields];
    this.filterTable();
  }

  async printTable() {
    await this.preparePdfImages();
    const selectedCount = this.selectedFields.filter(f => f.selected).length;

    // Adjust this number depending on layout width
    const orientation = selectedCount > 7 ? 'landscape' : 'portrait';

    let printContent = `
  <html>
  <head>
    <title>Stock Out List</title>

    <style>

      body {
        font-family: Arial, sans-serif;
      }

      /* ===== TABLE GRID ===== */

      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 12px;
      }

      th,
      td {
        border-right: 2px solid #000;
        border-bottom: 2px solid #000;
        padding: 8px;
        text-align: left;
        vertical-align: middle;
        white-space: nowrap;
      }

      th:first-child,
      td:first-child {
        border-left: 2px solid #000;
      }

      thead th {
        border-top: 2px solid #000;
        background-color: #f2f2f2;
        font-weight: 600;
      }

      tbody tr:last-child td {
        border-bottom: 2px solid #000;
      }

      /* ===== PRINT ===== */

      @media print {

       @page {
  size: auto;
  margin: 8mm;
}


        body {
          margin: 0;
        }

        thead {
          display: table-header-group;
        }

        tr {
          page-break-inside: avoid;
        }
      }

    </style>

  </head>

  <body>

    <h3>Stock Out List</h3>

    <table>
      <thead>
        <tr>
          <th>No.</th>
  `;

    // 🧾 HEADERS
    this.selectedFieldss.forEach((field) => {
      printContent += `<th>${field.label}</th>`;
    });

    printContent += `
        </tr>
      </thead>
      <tbody>
  `;

    // 📦 ROWS
    const data = this.getExportSourceData();
    data.forEach((item, index) => {
      printContent += `
      <tr>
        <td>${(this.currentPage - 1) * this.itemsPerPage + index + 1}</td>
    `;

      this.selectedFieldss.forEach((field) => {
        let displayValue = '';
        if (field.isImage && item.__img) {
          displayValue = `<img src="${item.__img}" style="max-height: 50px; width: auto;" />`;
        } else {
          displayValue = this.getDisplayValue(item[field.key]);
        }
        printContent += `<td>${displayValue}</td>`;
      });

      printContent += `
      </tr>
    `;
    });

    printContent += `
      </tbody>
    </table>

  </body>
  </html>
  `;

    // 🖨 OPEN PRINT WINDOW
    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 200);
      };
    }
  }


  onFieldToggle(field: any) {
    this.saveSelectedFields();
    this.filterTable();
  }

  getDisplayValue(value: any): string {
    if (value == null) return '';
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }

  /* ============================================================
     IMAGE PROXY HELPERS
  ============================================================ */

  proxyImageUrl(url: string): string {
    if (!url) return url;
    if (url.includes('/api/image-proxy/')) return url;

    // Extract the relative path after /storage/
    const match = url.match(/\/storage\/(.+)$/);
    if (!match) return url;

    const backendBase = environment.apiBaseUrl.replace(/\/api\/$/, '');
    return `${backendBase}/api/image-proxy/${match[1]}`;
  }

  /* ============================================================
     LOAD IMAGE AS BASE64
  ============================================================ */

  async loadImageBase64(url: string): Promise<string | null> {
    try {
      const proxiedUrl = this.proxyImageUrl(url);
      console.log('Loading image:', proxiedUrl);

      const res = await fetch(proxiedUrl, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (!res.ok) {
        console.error(' HTTP error:', res.status);
        throw new Error(res.statusText);
      }

      const blob = await res.blob();

      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Image load FAILED:', url, err);
      return null;
    }
  }

  /* ============================================================
     PRELOAD ALL PDF IMAGES
  ============================================================ */

  async preparePdfImages(): Promise<void> {
    console.log(' Preparing images for PDF...');
    const dataToProcess = this.getExportSourceData();
    for (const row of dataToProcess as any[]) {
      if (!row.stout_image) continue;
      console.log('➡ Row:', row.id, row.stout_image);
      row.__img = await this.loadImageBase64(row.stout_image);
      console.log(row.__img ? 'Image OK' : ' Image NULL');
    }
    console.log(' Image preload finished');
  }
  private getExportSourceData(): any[] {

    const hasFilter =
      this.startDate ||
      this.endDate ||
      this.globalSearchText ||
      Object.values(this.searchValues).some(v => v);

    return hasFilter
      ? this.filteredStockOutList
      : this.stockOutList;
  }

}
